import React, { useState, type PropsWithChildren } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";

import LoadableButton, {
  type LoadableButtonProps,
} from "src/components/LoadableButton";
import { bankAccountsWithOutIntegrationSelector } from "src/store/bankAccount/selector";
import { getCallableErrorMessage } from "src/utils/callableErrors";

const CREATE_NEW_VALUE = "__create_new__";

/**
 * Hook to check if there are existing sale platforms without integrations.
 * Used to determine whether to show the platform selection dialog or skip directly to connect.
 */
export function useHasExistingSalePlatforms() {
  const bankAccounts = useSelector(bankAccountsWithOutIntegrationSelector);
  const existingSalePlatforms = bankAccounts?.filter(
    (acc) => acc.type === "sale-platform" && !acc.hidden
  );
  return existingSalePlatforms && existingSalePlatforms.length > 0;
}

const useStyles = makeStyles((theme) => ({
  helperText: {
    marginTop: theme.spacing(2),
  },
  subheader: {
    backgroundColor: theme.palette.background.paper,
  },
}));

export interface AddNewSalePlatformIntegrationDialogProps {
  open: boolean;
  onClose: () => void;
  onConnect: (bankAccountId?: string | undefined) => Promise<void>;
  loading?: boolean;
  platformName?: string;
}

function Content({
  onClose,
  onConnect,
  loading,
  platformName = "Sale Platform",
}: Pick<
  AddNewSalePlatformIntegrationDialogProps,
  "onClose" | "onConnect" | "loading" | "platformName"
>) {
  const classes = useStyles();
  const bankAccounts = useSelector(bankAccountsWithOutIntegrationSelector);
  const existingSalePlatforms = bankAccounts?.filter(
    (acc) => acc.type === "sale-platform" && !acc.hidden
  );
  const hasExistingPlatforms =
    existingSalePlatforms && existingSalePlatforms.length > 0;

  const [selectedValue, setSelectedValue] = useState<string>(CREATE_NEW_VALUE);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const bankAccountId =
      selectedValue === CREATE_NEW_VALUE ? undefined : selectedValue;
    setSubmitting(true);
    try {
      await onConnect(bankAccountId);
      onClose();
    } catch (error) {
      toast.error(getCallableErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate autoComplete="off">
      <DialogTitle>Connect {platformName}</DialogTitle>
      <DialogContent>
        <FormControl variant="outlined" fullWidth>
          <TextField
            select
            label="Sale Platform"
            variant="outlined"
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
          >
            <MenuItem value={CREATE_NEW_VALUE}>Add New</MenuItem>
            {hasExistingPlatforms && [
              <ListSubheader key="divider" className={classes.subheader}>
                Or use existing
              </ListSubheader>,
              ...existingSalePlatforms.map((platform) => (
                <MenuItem key={platform.id} value={platform.id}>
                  {platform.name}
                </MenuItem>
              )),
            ]}
          </TextField>
        </FormControl>
        <Box className={classes.helperText}>
          <Typography variant="body2" color="textSecondary">
            {selectedValue === CREATE_NEW_VALUE
              ? "Select existing to tag new data with that platform name."
              : `New data will be tagged with "${
                  existingSalePlatforms.find((p) => p.id === selectedValue)
                    ?.name
                }".`}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadableButton
          color="primary"
          variant="contained"
          type="submit"
          loading={loading || submitting}
        >
          Connect
        </LoadableButton>
      </DialogActions>
    </form>
  );
}

function AddNewSalePlatformIntegrationDialog({
  open,
  onClose,
  onConnect,
  loading,
  platformName,
}: AddNewSalePlatformIntegrationDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Content
        onClose={onClose}
        onConnect={onConnect}
        loading={loading}
        platformName={platformName}
      />
    </Dialog>
  );
}

async function runConnectWithToast(
  onConnect: (bankAccountId?: string | undefined) => Promise<void>,
  bankAccountId?: string
) {
  try {
    await onConnect(bankAccountId);
  } catch (error) {
    toast.error(getCallableErrorMessage(error));
    throw error;
  }
}

export function AddNewSalePlatformIntegrationButton({
  onConnect,
  children,
  platformName,
  ...props
}: PropsWithChildren<{
  onConnect: (bankAccountId?: string | undefined) => Promise<void>;
  platformName?: string;
}> &
  LoadableButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasExistingPlatforms = useHasExistingSalePlatforms();

  const handleDirectConnect = async () => {
    setLoading(true);
    try {
      await runConnectWithToast(onConnect, undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadableButton
        {...props}
        loading={loading}
        onClick={async () => {
          if (hasExistingPlatforms) {
            setOpen(true);
            return;
          }
          await handleDirectConnect();
        }}
      >
        {children}
      </LoadableButton>
      <AddNewSalePlatformIntegrationDialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        onConnect={async (bankAccountId) => {
          setLoading(true);
          try {
            await onConnect(bankAccountId);
          } finally {
            setLoading(false);
          }
        }}
        platformName={platformName}
      />
    </>
  );
}

export default AddNewSalePlatformIntegrationDialog;
